import {
  type ApolloError,
  gql,
  useApolloClient,
  useMutation,
} from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type {
  SmbAttachmentStatus,
  SmbConversationStatus,
  Transaction,
  TransactionSide,
} from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";
import { BANK_TRANSACTION_FRAGMENT } from "./graphql";

type BankTransactionUpdateFields = {
  amount?: number;
  counterpartyName?: string;
  counterpartyIban?: string;
  purpose?: string;
  currency?: string;
  side?: TransactionSide;
  bookingDate?: Date;
  smbAttachmentStatus?: SmbAttachmentStatus;
  smbConversationStatus?: SmbConversationStatus;
};

type UpdateBankTransactionInput = {
  bankTransactionIds: string[];
  updatedFields: BankTransactionUpdateFields;
};

export const useUpdateBankTransaction = (): [
  (input: UpdateBankTransactionInput) => Promise<Transaction[]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const client = useApolloClient();

  const [updateBankTransactions, { loading, error }] = useMutation<{
    updateBankTransactions: Transaction[];
  }>(UPDATE_BANK_TRANSACTIONS_MUTATION, {
    refetchQueries: [GET_ALL_TRANSACTIONS],
    awaitRefetchQueries: true,
    update: (cache, { data }, { variables }) => {
      if (!data?.updateBankTransactions || !variables?.bankTransactionIds) {
        return;
      }

      variables.bankTransactionIds.forEach((bankTxnId: string) => {
        const existing = cache.readFragment({
          id: `Transaction:${bankTxnId}`,
          fragment: BANK_TRANSACTION_FRAGMENT,
          fragmentName: "BankTransaction",
        });

        if (existing) {
          cache.writeFragment({
            id: `Transaction:${bankTxnId}`,
            fragment: BANK_TRANSACTION_FRAGMENT,
            fragmentName: "BankTransaction",
            data: {
              ...existing,
              ...variables.updatedFields,
            },
          });
        }
      });
    },
    optimisticResponse: ({ bankTransactionIds, updatedFields }) => {
      const optimisticTransactions = bankTransactionIds
        .map((bankTxnId: string) => {
          const cacheId = client.cache.identify({
            __typename: "Transaction",
            bankTxnId: bankTxnId,
          });

          const existingTransaction = client.cache.readFragment({
            id: cacheId,
            fragment: BANK_TRANSACTION_FRAGMENT,
            fragmentName: "BankTransaction",
          });

          if (!existingTransaction) {
            return null;
          }

          return {
            __typename: "Transaction" as const,
            ...existingTransaction,
            ...updatedFields,
          };
        })
        .filter(Boolean);

      return {
        updateBankTransactions: optimisticTransactions,
      };
    },
  });

  const handleUpdateBankTransaction = async (
    input: UpdateBankTransactionInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await updateBankTransactions({
        variables: {
          bankTransactionIds: input.bankTransactionIds,
          updatedFields: input.updatedFields,
        },
      });

      if (!response) {
        throw new Error("No response from updateBankTransactions");
      }

      toast.dismiss(toastId);

      return response.updateBankTransactions;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          module: "transactions",
          action: "update",
        },
      });
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleUpdateBankTransaction, { loading, error }];
};

export const UPDATE_BANK_TRANSACTIONS_MUTATION = gql`
  mutation UpdateBankTransactions(
    $bankTransactionIds: [String!]!
    $updatedFields: BankTransactionInput!
  ) {
    updateBankTransactions(
      bankTransactionIds: $bankTransactionIds
      updatedFields: $updatedFields
    ) {
      bankTxnId
      tenantId
      status
      amount
      currency
      createdAt
      counterpartyName
      counterpartyIban
      purpose
      executionDate
      side
      bookingDate
      valueDate
      smbAttachmentStatus
      smbConversationStatus
      invoices {
        invoiceId
        invoiceNo
        totalAmount
        currency
        supplierName
        invoiceDate
      }
    }
  }
`;
